import { useEffect } from 'react';
import { disableNetwork, enableNetwork, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';

/**
 * useFirestoreSyncMonitor — two pieces of multi-device sync hardening
 * landed in one hook so App.js only has to wire one thing in.
 *
 *   1. Force-reconnect on visibility change. iOS / Android suspend
 *      PWAs when backgrounded; the Firestore long-poll / WebSocket
 *      drops, and iOS in particular is slow (~10 min, sometimes more)
 *      to reopen it on foreground. The result was the symptom Walter
 *      hit: desktop edits took 10+ min to appear on his phones. On
 *      visibility=visible AFTER the tab has been hidden for >10s we
 *      explicitly disableNetwork(db) → enableNetwork(db). Firestore's
 *      resume tokens mean re-fetched docs are deltas, not full
 *      re-reads, so the read-cost overhead is small (a percent or
 *      two, not a multiplier).
 *
 *   2. Stale-listener detection. Each onSnapshot callback's metadata
 *      tells us whether the snapshot was served from local cache
 *      (`fromCache`) or directly from the server. While fromCache
 *      is true, the client is serving potentially stale data — and
 *      any writes made during this window risk being stale-base
 *      last-write-wins overwrites of newer cloud state. We subscribe
 *      with includeMetadataChanges to a stable doc (the user's
 *      preferences/main) and publish a module-level "isStale"
 *      signal that the SaveIndicator reads to show a "reconnecting"
 *      variant.
 *
 * Walter 2026-05-16: shipped after data-loss reports on iPhone (lost
 * line of list content, 10+ minute lag between devices). Pair: fix
 * #1 + #2 of the multi-device sync hardening triage.
 */

// Module-singleton state — same pattern as useInstallPrompt. The
// SaveIndicator subscribes via getSyncState/subscribeSync without
// prop drilling.
const state = {
  isStale: false,
  lastSyncedAt: null,
  // Walter 2026-06-13: ground-truth extension-block detection (drives
  // ExtensionBlockHint). `everSynced` flips true the first time a SERVER
  // (non-cache) snapshot arrives — proof the Firestore WebChannel works;
  // once true it suppresses the block banner for the session. The watchdog
  // below sets `connectionBlocked` when we're signed-in + online +
  // foregrounded but have NEVER synced past a grace window — the fingerprint
  // of an ad-blocker blocking the WebChannel (a slow-but-working network
  // still delivers server data inside the window; a blocked one never does).
  everSynced: false,
  connectionBlocked: false,
};
const listeners = new Set();
const notify = () => { for (const cb of listeners) cb(); };

export function getSyncState() {
  return state;
}

export function subscribeSync(cb) {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

export function useFirestoreSyncMonitor(userId) {
  // (1) Force reconnect on visibility change after a non-trivial hide.
  useEffect(() => {
    let backgroundedAt = null;
    let lastReconnectAt = 0;

    const handler = async () => {
      if (document.visibilityState === 'hidden') {
        backgroundedAt = Date.now();
        return;
      }
      // visible — was the tab away long enough to warrant a reconnect?
      if (!backgroundedAt) return;
      const awayMs = Date.now() - backgroundedAt;
      backgroundedAt = null;
      // <10s away = listener probably still alive. >10s = worth
      // bouncing the network. Also rate-limit so a rapid foreground/
      // background flutter doesn't fire repeatedly.
      if (awayMs < 10_000) return;
      if (Date.now() - lastReconnectAt < 20_000) return;
      lastReconnectAt = Date.now();
      try {
        await disableNetwork(db);
        await enableNetwork(db);
      } catch (err) {
        console.warn('[firestore reconnect] failed', err);
      }
    };

    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, []);

  // (2) Stale-listener detection. Subscribe to a small, stable doc
  // (users/{uid}/preferences/main) with metadata changes enabled.
  // The metadata.fromCache flag flips when the client is serving
  // from local cache (stale) vs the server (fresh).
  useEffect(() => {
    if (!userId) return undefined;
    // New session/user — reset the connection-health signals so a prior
    // session's "synced" state can't mask a fresh block (or vice-versa).
    state.everSynced = false;
    state.connectionBlocked = false;
    notify();
    const ref = doc(db, 'users', userId, 'preferences', 'main');
    const unsub = onSnapshot(
      ref,
      { includeMetadataChanges: true },
      (snap) => {
        const nextStale = snap.metadata.fromCache === true;
        let changed = false;
        if (state.isStale !== nextStale) { state.isStale = nextStale; changed = true; }
        if (!nextStale) {
          // Fresh server snapshot — the connection demonstrably works.
          state.lastSyncedAt = Date.now();
          if (!state.everSynced) { state.everSynced = true; changed = true; }
          if (state.connectionBlocked) { state.connectionBlocked = false; changed = true; }
        }
        if (changed) notify();
      },
      (err) => {
        console.warn('[firestore sync monitor] subscribe failed', err);
      },
    );
    return unsub;
  }, [userId]);

  // (3) Extension-block watchdog (Walter 2026-06-13). When signed in, online,
  // and foregrounded but we've NEVER received a server snapshot past the
  // grace window, the WebChannel is almost certainly blocked by an
  // ad-blocker / privacy extension — a slow-but-working network still
  // delivers server data inside the window; a blocked one never does. The
  // `everSynced` master flag (set above) means once real data flows we never
  // flag, which is what keeps this from false-positiving on slow networks.
  useEffect(() => {
    if (!userId) return undefined;
    // Walter 2026-07-04: was 15s — too short. Jeffrey (macOS Safari, no
    // extensions) saw the "blocked" banner because Safari's first WebChannel
    // server snapshot can take >15s; it cleared once sync landed ("went away
    // on its own"). A genuinely blocked connection NEVER syncs, so widening
    // the grace to 40s only delays the (correct) warning for real blocks while
    // killing the false-positive on slow-to-connect browsers.
    const GRACE_MS = 40000;
    const startedAt = Date.now();
    const evaluate = () => {
      const blocked =
        !state.everSynced &&
        (Date.now() - startedAt > GRACE_MS) &&
        (typeof navigator === 'undefined' || navigator.onLine !== false) &&
        (typeof document === 'undefined' || document.visibilityState === 'visible');
      if (blocked !== state.connectionBlocked) {
        state.connectionBlocked = blocked;
        notify();
      }
    };
    const iv = setInterval(evaluate, 3000);
    return () => clearInterval(iv);
  }, [userId]);
}
